import { useEffect, useState } from "react";

export type MarketChart = {
  status: "idle" | "loading" | "ready" | "error";
  /** USD prices over the last day, oldest first. */
  points: number[];
};

const CHART_TTL_MS = 5 * 60_000;
const charts = new Map<string, { points: number[]; at: number }>();

function chartPoints(data: unknown): number[] {
  const prices = (data as { prices?: unknown })?.prices;
  if (!Array.isArray(prices)) return [];
  return prices
    .filter(
      (point): point is [number, number] =>
        Array.isArray(point) &&
        typeof point[0] === "number" &&
        typeof point[1] === "number" &&
        Number.isFinite(point[0]) &&
        Number.isFinite(point[1]) &&
        point[1] > 0,
    )
    .sort((a, b) => a[0] - b[0])
    .map((point) => point[1]);
}

/** Public one-day chart for a CoinGecko id; null fetches nothing. */
export function useMarketChart(id: string | null) {
  const [chart, setChart] = useState<MarketChart>({
    status: "idle",
    points: [],
  });
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!id) {
      setChart({ status: "idle", points: [] });
      return;
    }
    const hit = charts.get(id);
    if (hit && Date.now() - hit.at < CHART_TTL_MS) {
      setChart({ status: "ready", points: hit.points });
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10_000);
    setChart({ status: "loading", points: [] });
    fetch(`/api/proxy/coingecko?path=/api/v3/coins/${id}/market_chart`, {
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error("Chart unavailable");
        return response.json();
      })
      .then((data) => {
        const points = chartPoints(data);
        if (points.length < 2) throw new Error("Chart unavailable");
        charts.set(id, { points, at: Date.now() });
        if (!cancelled) setChart({ status: "ready", points });
      })
      .catch(() => {
        if (!cancelled) setChart({ status: "error", points: [] });
      })
      .finally(() => clearTimeout(timeout));
    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [id, attempt]);
  return { ...chart, retry: () => setAttempt((count) => count + 1) };
}
